/**
 * The Roles screen (§A PR-2) — what each role in this account can do, on the
 * four role routes (P15–P18).
 *
 * **Unpaginated by contract**: `GET /roles` answers a bare `{ data }` with no
 * `meta` (`roles.routes.ts`), so there is no `Pagination` and no page state in
 * the URL — a role list is a dozen rows, not an estate.
 *
 * **Writes are gated on `PERMISSION.usersWrite`, because no `roles.write` key
 * exists in the catalog** — the API itself guards P16–P18 with
 * `requirePermission('users.write')` (`roles.routes.ts`), on the reasoning
 * that shaping roles *is* shaping users. Inventing a stricter local key would
 * hide controls the server would happily serve.
 *
 * **System roles are read-only.** The server refuses to edit or delete them
 * independently; hiding the buttons here is the honesty rule (criterion 11):
 * absent, not disabled — a permanent "Edit" that always errors teaches an
 * administrator the screen is broken, not that the role is protected.
 */

import type { RoleDto } from '@rd/shared';
import { useState, type ReactElement } from 'react';

import { useAuth } from '../../auth/AuthProvider.js';
import { PERMISSION } from '../../auth/keys.js';
import { Badge } from '../../components/Badge.js';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorState } from '../../components/ErrorState.js';
import { ConfirmDialog } from '../../components/Modal.js';
import { PageHeader } from '../../components/PageHeader.js';
import { Skeleton } from '../../components/Skeleton.js';
import { Table, type Column } from '../../components/Table.js';
import { useToast } from '../../components/Toast.js';

import { RoleFormModal } from './RoleFormModal.js';
import { useDeleteRole, useRoles } from './useRoles.js';

/**
 * What the delete confirmation says. Written as a function so the
 * `userCount > 0` warning is a property of the copy, not an extra dialog: an
 * administrator deleting "Fleet manager" must read *before confirming* that
 * three people are about to lose whatever access it granted.
 */
function deleteBody(role: RoleDto): string {
  const base = `The role "${role.name}" will be permanently deleted.`;
  if (role.userCount === 0) return `${base} Nobody currently holds it.`;
  const people = role.userCount === 1 ? '1 person currently holds' : `${role.userCount} people currently hold`;
  return `${base} ${people} this role and will lose the access it grants. This cannot be undone.`;
}

export function RoleListPage(): ReactElement {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const query = useRoles();
  const deleteRole = useDeleteRole();

  // See the header: `users.write` is the key the API enforces on role writes —
  // there is no `roles.write` in the catalog to gate on instead.
  const canWrite = hasPermission(PERMISSION.usersWrite);

  /** `{ role: null }` creates; a row edits it. `null` = closed. */
  const [modal, setModal] = useState<{ role: RoleDto | null } | null>(null);
  const [deleting, setDeleting] = useState<RoleDto | null>(null);

  const confirmDelete = async (): Promise<void> => {
    if (deleting === null) return;
    try {
      await deleteRole.mutateAsync(deleting.key);
      toast.push('success', `"${deleting.name}" deleted.`);
      setDeleting(null);
    } catch {
      // The dialog stays open: the administrator decides whether to retry or
      // cancel, rather than the failure silently closing their confirmation.
      toast.push('error', 'The role could not be deleted.');
    }
  };

  const columns: Column<RoleDto>[] = [
    {
      key: 'name',
      header: 'Role',
      render: (role) => <span className="font-medium text-body">{role.name}</span>,
    },
    {
      key: 'key',
      header: 'Key',
      kind: 'mono',
      render: (role) => <span className="text-sm">{role.key}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      kind: 'status',
      render: (role) =>
        role.isSystem ? (
          <Badge size="sm" tone="neutral">
            System
          </Badge>
        ) : (
          <span className="text-muted">Custom</span>
        ),
    },
    {
      key: 'permissions',
      header: 'Permissions',
      kind: 'numeric',
      render: (role) => role.permissions.length,
    },
    {
      key: 'userCount',
      header: 'People',
      kind: 'numeric',
      render: (role) => role.userCount,
    },
    {
      key: 'actions',
      header: 'Actions',
      kind: 'actions',
      render: (role) => {
        // System roles are the platform's own — read-only everywhere, so no
        // affordance at all rather than one that always errors.
        if (!canWrite || role.isSystem) return <span className="text-muted">—</span>;
        return (
          <div className="flex items-center justify-end gap-[var(--space-2)]">
            <Button variant="ghost" size="sm" onClick={() => setModal({ role })}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDeleting(role)}>
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Roles"
        description="Named bundles of permissions. Assign them to people on the Users screen."
        actions={
          canWrite ? <Button onClick={() => setModal({ role: null })}>Create role</Button> : undefined
        }
      />

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : null}

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}

      {query.data ? (
        <Table
          caption="Roles, system first"
          columns={columns}
          rows={query.data}
          rowKey={(role) => role.key}
          empty={
            <EmptyState
              title="No roles"
              body="The system roles should always be here — try reloading."
            />
          }
        />
      ) : null}

      <RoleFormModal
        open={modal !== null}
        role={modal?.role ?? null}
        onClose={() => setModal(null)}
      />

      {/* `destructive`, through the Phase 04 primitive: the dialog decides how
          a dangerous confirmation looks, not this screen (criterion 25). */}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete this role?"
        body={deleting === null ? '' : deleteBody(deleting)}
        confirmLabel="Delete role"
        destructive
        busy={deleteRole.isPending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
